import { useEffect, useRef, useState } from "react";
import type {
  MemberCommunicationAttachmentDto,
  MemberCommunicationAttachmentType,
  ViewerMemberCommunicationDto
} from "@madamuh/domain";
import { useNavigate, useParams } from "react-router-dom";
import {
  createFamilyCommunicationAttachmentAccessUrl,
  fetchFamilyMemberCommunicationDetail,
  markFamilyMemberCommunicationRead
} from "../../shared/api/memberCommunication";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { canUseFamilyMode } from "../../shared/session/modeRouting";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { DEFAULT_TENANT_TIMEZONE } from "../../shared/lib/tenantDateTime";
import { formatCommunicationPublishedAtJa } from "../../shared/lib/memberCommunicationDisplay";
import { GENERIC_TENANT_LABELS, getTenantLabels } from "../../shared/lib/tenantLabels";
import { FamilyAppShell } from "../../shared/ui/family/FamilyAppShell";
import {
  COMMUNICATION_ATTACHMENT_LABELS,
  CommunicationThumbnailButton,
  CommunicationUnreadBadge,
  MemberCommunicationVideoThumbnail,
  describeCommunicationCategory,
  getCommunicationTitle
} from "../../shared/ui/MemberCommunicationCard";

type AttachmentOpenState = {
  attachmentId: string;
  url: string;
  type: MemberCommunicationAttachmentType;
  posterUrl: string | null;
} | null;

function actionLabel(attachment: MemberCommunicationAttachmentDto): string {
  if (attachment.attachmentType === "image") return "画像を表示";
  if (attachment.attachmentType === "video") return "動画を再生";
  return "PDFを開く";
}

function AttachmentAction({
  tenantSlug,
  communicationId,
  attachment,
  onOpen
}: {
  tenantSlug: string;
  communicationId: string;
  attachment: MemberCommunicationAttachmentDto;
  onOpen: (state: AttachmentOpenState) => void;
}) {
  const navigate = useNavigate();
  const [state, setState] = useState<"idle" | "loading" | "error">("idle");
  const [message, setMessage] = useState<string | null>(null);

  async function handleClick(thumbnailUrl: string | null = null) {
    setState("loading");
    setMessage(null);

    const pdfWindow = attachment.attachmentType === "pdf" ? window.open("", "_blank", "noopener,noreferrer") : null;

    try {
      const response = await createFamilyCommunicationAttachmentAccessUrl(tenantSlug, communicationId, attachment.attachmentId);
      if (attachment.attachmentType === "pdf") {
        if (!pdfWindow) {
          setState("error");
          setMessage("PDFを開けませんでした。ブラウザの設定を確認してください。");
          return;
        }
        pdfWindow.location.href = response.accessUrl;
        setState("idle");
        return;
      }
      onOpen({ attachmentId: attachment.attachmentId, url: response.accessUrl, type: attachment.attachmentType, posterUrl: thumbnailUrl });
      setState("idle");
    } catch (error) {
      pdfWindow?.close();
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }
      setState("error");
      setMessage(getProtectedApiErrorMessage(error, "添付ファイルを開けませんでした。もう一度お試しください。"));
    }
  }

  return (
    <article className="family-list-card" style={{ gap: 12 }}>
      {attachment.attachmentType === "video" ? (
        <MemberCommunicationVideoThumbnail
          communicationId={communicationId}
          attachment={attachment}
          tenantSlug={tenantSlug}
          surface="family"
          onOpen={(_, thumbnailUrl) => void handleClick(thumbnailUrl)}
        />
      ) : (
        <CommunicationThumbnailButton
          communicationId={communicationId}
          attachment={attachment}
          tenantSlug={tenantSlug}
          surface="family"
          onOpen={(_, thumbnailUrl) => void handleClick(thumbnailUrl)}
        />
      )}
      <span className="family-list-card__main">
        <strong>{COMMUNICATION_ATTACHMENT_LABELS[attachment.attachmentType]}</strong>
        <span className="family-list-card__support family-break-anywhere">
          {attachment.originalFileName ?? "添付ファイル"}
        </span>
        <button
          type="button"
          onClick={() => void handleClick()}
          disabled={state === "loading"}
          aria-label={`${attachment.originalFileName ?? COMMUNICATION_ATTACHMENT_LABELS[attachment.attachmentType]}を開く`}
        >
          {state === "loading" ? "読み込んでいます..." : actionLabel(attachment)}
        </button>
        {state === "error" ? (
          <span style={{ display: "grid", gap: 6 }}>
            <span className="family-text-muted" role="alert">{message}</span>
            <button type="button" onClick={() => void handleClick()}>もう一度試す</button>
          </span>
        ) : null}
      </span>
    </article>
  );
}

export function FamilyMemberCommunicationDetailPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant", communicationId = "" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [communication, setCommunication] = useState<ViewerMemberCommunicationDto | null>(null);
  const [openAttachment, setOpenAttachment] = useState<AttachmentOpenState>(null);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const markedReadForId = useRef<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hasFamilyAccessRole = canUseFamilyMode(session);
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;

  useEffect(() => {
    if (!isSessionReady || !hasFamilyAccessRole || !communicationId) {
      return;
    }

    let cancelled = false;

    async function loadDetail() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const nextCommunication = await fetchFamilyMemberCommunicationDetail(tenantSlug, communicationId);
        if (cancelled) {
          return;
        }
        setCommunication(nextCommunication);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }
        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }
        setLoadState("error");
        setLoadError(getProtectedApiErrorMessage(error, "連絡内容の取得に失敗しました。"));
      }
    }

    void loadDetail();

    return () => {
      cancelled = true;
    };
  }, [communicationId, hasFamilyAccessRole, isSessionReady, navigate, tenantSlug]);

  useEffect(() => {
    if (!communication || markedReadForId.current === communication.communicationId) {
      return;
    }
    markedReadForId.current = communication.communicationId;
    void markFamilyMemberCommunicationRead(tenantSlug, communication.communicationId)
      .then((updated) => {
        setCommunication(updated);
      })
      .catch(() => {
        // Detail load succeeded; read state is best-effort and idempotent.
      });
  }, [communication, tenantSlug]);

  useEffect(() => {
    if (openAttachment?.type !== "video" || !videoRef.current) {
      return;
    }
    void videoRef.current.play().catch(() => {
      // Native controls remain visible if the browser blocks programmatic playback.
    });
  }, [openAttachment]);

  const title = communication ? getCommunicationTitle(communication) : "連絡事項";

  return (
    <FamilyAppShell
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      activeTab="communications"
      navigationCountsEnabled={hasFamilyAccessRole}
    >
      <div className="family-page-stack">
        {routeStatus === "checking" || sessionStatus === "loading" ? (
          <article className="family-surface family-notice">
            <p className="family-text-muted">セッションを確認しています。</p>
          </article>
        ) : null}
        {routeStatus === "redirecting" ? (
          <article className="family-surface family-notice">
            <p className="family-text-muted">利用開始へ移動しています。</p>
          </article>
        ) : null}
        {sessionStatus === "error" ? (
          <article className="family-surface family-notice family-notice--warning">
            <p className="family-text-muted">セッション確認に失敗しました。{errorMessage}</p>
          </article>
        ) : null}

        <section className="family-surface family-section">
          <h2 className="family-page-title">連絡内容</h2>
          <p className="family-page-intro">クラブや{tenantLabels.teacher}から届いた連絡の詳細です。</p>
        </section>

        {loadState === "loading" ? (
          <article className="family-surface family-notice">
            <p className="family-text-muted">連絡内容を読み込んでいます。</p>
          </article>
        ) : null}
        {loadError ? (
          <article className="family-surface family-notice family-notice--warning">
            <p className="family-text-muted">{loadError}</p>
          </article>
        ) : null}

        {communication ? (
          <>
            <section className="family-surface family-section" style={{ display: "grid", gap: 10 }}>
              <span style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <strong className="family-break-anywhere" style={{ fontSize: 24 }}>{title}</strong>
                <CommunicationUnreadBadge isUnread={communication.isUnread} />
              </span>
              <div className="family-text-muted">
                {describeCommunicationCategory(communication.category, tenantLabels)} / {communication.targetLabel}
              </div>
              <div className="family-text-muted">
                公開日時: {formatCommunicationPublishedAtJa(communication.publishedAt ?? communication.publishFrom ?? communication.updatedAt, DEFAULT_TENANT_TIMEZONE)}
              </div>
              {communication.body ? (
                <div className="family-break-anywhere" style={{ whiteSpace: "pre-wrap" }}>{communication.body}</div>
              ) : (
                <p className="family-text-muted">本文はありません。</p>
              )}
            </section>

            <section className="family-surface family-section" style={{ display: "grid", gap: 12 }}>
              <h3 style={{ margin: 0, fontSize: 20 }}>添付ファイル</h3>
              {communication.attachments.length === 0 ? <p className="family-empty-state">添付ファイルはありません。</p> : null}
              {communication.attachments.map((attachment) => (
                <AttachmentAction
                  key={attachment.attachmentId}
                  tenantSlug={tenantSlug}
                  communicationId={communication.communicationId}
                  attachment={attachment}
                  onOpen={(nextOpenAttachment) => {
                    setMediaError(null);
                    setOpenAttachment(nextOpenAttachment);
                  }}
                />
              ))}
            </section>

            {openAttachment?.type === "image" ? (
              <div
                role="dialog"
                aria-modal="true"
                aria-label="画像プレビュー"
                style={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 80,
                  display: "grid",
                  gap: 12,
                  placeItems: "center",
                  padding: 16,
                  background: "rgba(0,0,0,0.72)"
                }}
              >
                <div style={{ width: "100%", maxWidth: 720, display: "grid", gap: 12 }}>
                  <button type="button" onClick={() => setOpenAttachment(null)}>閉じる</button>
                  <img
                    src={openAttachment.url}
                    alt="添付画像"
                    style={{ width: "100%", maxHeight: "72vh", objectFit: "contain", background: "#111" }}
                    onError={() => setMediaError("画像を表示できませんでした。もう一度試してください。")}
                  />
                  {mediaError ? <p style={{ color: "#fff" }}>{mediaError}</p> : null}
                </div>
              </div>
            ) : null}

            {openAttachment?.type === "video" ? (
              <section className="family-surface family-section" style={{ display: "grid", gap: 12 }}>
                <h3 style={{ margin: 0, fontSize: 20 }}>動画を再生</h3>
                <video
                  ref={videoRef}
                  src={openAttachment.url}
                  controls
                  playsInline
                  poster={openAttachment.posterUrl ?? undefined}
                  style={{ width: "100%", maxHeight: "70vh", background: "#111", borderRadius: 12 }}
                  onError={() => setMediaError("動画を再生できませんでした。もう一度試してください。")}
                />
                {mediaError ? <p className="family-text-muted" role="alert">{mediaError}</p> : null}
              </section>
            ) : null}
          </>
        ) : null}
      </div>
    </FamilyAppShell>
  );
}
